import {
  InspectorControlsAppearance,
  InspectorControlsGeneral,
  InspectorTabs,
  InspectorControlsDeveloper,
} from '@components/inspector-tabs';
import { SpacingTop, SpacingBottom } from '@components/spacing-selector';
import { generateSpacingClasses } from '@components/spacing-selector/utils';
import Visibility from '@components/visibility';
import BackgroundTypes from '@components/background-types';
import ClassDropdown, { getBlockClassNames } from '@components/class-dropdown';
import Label from '@components/block-label';
import BackgroundTypesSave from '@components/background-types/save';
import {
  FocusableIcon,
  FocusableIconSwitcher,
} from '@components/focusable-block-icon';
import { title } from './block.json';
import sectionIcon from './icon';
import { BERG_STORE, DEFAULT_CSS_CLASS } from '@constants';
import { setUniqueIdAttribute } from '@helpers/utils.js';
import classnames from 'classnames';

import './editor.scss';

const {
  i18n: { __ },
  components: { PanelBody, RadioControl, TextControl },
  data: { select },
  element: { useEffect },
  blockEditor: { useBlockProps, InnerBlocks },
} = wp;

const Edit = (props) => {
  const { attributes, setAttributes, name } = props;
  const {
    blockId,
    containerType,
    blockVisibility,
    blockClassNames,
    backgroundType,
    backgroundGradientOptions,
    backgroundColorOptions,
    backgroundImageOptions,
    backgroundImageAndColorOptions,
    imageAsBackgroundOptions,
    imageGradientBackgroundOptions,
    backgroundVideoOptions,
    label,
    enableFocusableIcon,
    spacingTop,
    spacingBottom,
    anchorId,
    printBackgroundStyles,
  } = attributes;
  const containerTypeOptions = select(BERG_STORE).getBlockAttributeOptions(
    name,
    'containerType',
  );
  const ALLOWED_BLOCKS = [
    'core/columns',
    'core/group',
    'core/stack',
    'core/row',
    'b3rg/row',
  ];

  useEffect(() => {
    setUniqueIdAttribute(name, 'blockId', attributes, setAttributes);
  }, [name, attributes, setAttributes]);

  const defaultBlockCssClass = DEFAULT_CSS_CLASS.SECTION;
  const defaultBlockDynamicCssClass = `${defaultBlockCssClass}-${blockId}`;
  const classNames = getBlockClassNames(blockClassNames);
  const spacingTopClasses = generateSpacingClasses(spacingTop);
  const spacingBottomClasses = generateSpacingClasses(spacingBottom);

  const blockProps = useBlockProps({
    className: classnames(
      {
        [`${defaultBlockCssClass}`]: true,
        [`${defaultBlockDynamicCssClass}`]: defaultBlockDynamicCssClass,
        [`${classNames}`]: classNames,
        disable: !blockVisibility,
      },
      spacingTopClasses,
      spacingBottomClasses,
    ),
  });

  return (
    <>
      <InspectorTabs>
        <InspectorControlsGeneral>
          <Label {...{ label, setAttributes }} />
          <Visibility {...{ blockVisibility, setAttributes }} />
          <PanelBody title={__('Block Settings', 'b3rg')} initialOpen={false}>
            <TextControl
              label={__('Section ID', 'b3rg')}
              value={anchorId}
              help={__(
                'Enter a unique identifier for the section. ' +
                  "This will be converted to a URL-friendly slug and used as the section's ID for internal navigation.",
                'b3rg',
              )}
              onChange={(anchorId) => {
                setAttributes({ anchorId });
              }}
            />
            <RadioControl
              label={__('Container Type', 'b3rg')}
              selected={containerType}
              options={containerTypeOptions}
              onChange={(value) => {
                setAttributes({ containerType: value });
              }}
            />
          </PanelBody>
          <BackgroundTypes
            {...{
              blockId,
              setAttributes,
              backgroundType,
              backgroundGradientOptions,
              backgroundColorOptions,
              backgroundImageOptions,
              backgroundImageAndColorOptions,
              imageAsBackgroundOptions,
              imageGradientBackgroundOptions,
              backgroundVideoOptions,
              blockClassNames,
              printBackgroundStyles,
            }}
          />
        </InspectorControlsGeneral>
        <InspectorControlsAppearance>
          <ClassDropdown
            {...{
              blockClassNames,
              setAttributes,
              blockClassPrefix: DEFAULT_CSS_CLASS.SECTION,
            }}
          />
          <SpacingTop {...{ spacingTop, setAttributes }} />
          <SpacingBottom {...{ spacingBottom, setAttributes }} />
          <FocusableIconSwitcher
            {...{
              enableFocusableIcon,
              setAttributes,
            }}
          />
        </InspectorControlsAppearance>
        <InspectorControlsDeveloper>
          <PanelBody title={__('Developer', 'b3rg')} initialOpen={false}>
            <h4>Developer Settings</h4>
          </PanelBody>
        </InspectorControlsDeveloper>
      </InspectorTabs>
      <section {...blockProps}>
        <BackgroundTypesSave
          {...{
            blockId,
            defaultBlockCssClass,
            backgroundType,
            backgroundGradientOptions,
            backgroundColorOptions,
            backgroundImageOptions,
            backgroundImageAndColorOptions,
            imageAsBackgroundOptions,
            imageGradientBackgroundOptions,
            backgroundVideoOptions,
          }}
        />
        {enableFocusableIcon && (
          <FocusableIcon {...{ title, icon: sectionIcon }} />
        )}
        <div className={containerType}>
          <InnerBlocks allowedBlocks={ALLOWED_BLOCKS} />
        </div>
      </section>
    </>
  );
};

export default Edit;
